import _ from "underscore";
import globals from "web/script/modules/globals";

type URLSearchParamsLike = {
    [key: string]: string | string[];
};

export interface NotQuiteAURL {
    protocol: string;
    host: string;
    searchParams: URLSearchParams;
    path: string;
    hash: string;
}

export type URLQuery = URLSearchParams | URLSearchParamsLike | string;

declare global {
    interface URLSearchParams {
        /**
         * Note: This method is a deviation from the spec
         * @returns an object representing the current state of the URLSearchParams instance.
         */
        toJSON(): { [key: string]: string[] };
        /**
         * Utility function to remove one value from a parameter list
         */
        deleteValue(name: string, valueToRemove: string): void;
    }
}

URLSearchParams.prototype.toJSON = function(): { [key: string]: string[] } {
    let result = {};
    for (let key of this.keys()) {
        result[key] = this.getAll(key);
    }
    return result;
};

URLSearchParams.prototype.deleteValue = function deleteValue(
    name: string,
    valueToRemove: string
): void {
    if (!this.has(name)) {
        return;
    }

    let existing = this.getAll(name);
    this.delete(name);
    for (let value of existing) {
        if (value != valueToRemove) {
            this.append(name, value);
        }
    }
};

/**
 * Joins all arguments together ensuring there is a leading and trailing slash.
 * Any arguments that not non-empty strings are ignored.
 */
export function join(...args: string[]): string {
    let result = `/${args.filter((s) => !!s).join("/")}/`.replace(/\/+/g, "/");
    if (result == "/") {
        return "";
    }
    return result;
}

/**
 * Turns a URLSearchParams instance or a plain object into an encoded query string.
 */
export function toQueryString(params: URLSearchParams | URLSearchParamsLike): string {
    if (params instanceof URLSearchParams) {
        return params.toString();
    }

    let searchParams = new URLSearchParams();

    _.each(params, function(value, key) {
        if (_.isArray(value)) {
            value.forEach(searchParams.append.bind(searchParams, key));
        } else if (value != null) {
            searchParams.append(key, value);
        }
    });

    return searchParams.toString();
}

/**
 * Unparses an object with the same structure as returned from `parse` and returns a string.
 * For flexibility the `searchParams` property can be a URLSearchParams instance or just a plain object.
 */
export function unparse(parsed: NotQuiteAURL): string {
    let url = "";
    let queryString = toQueryString(parsed.searchParams);

    url += parsed.protocol ? parsed.protocol + "//" : "";
    url += parsed.host || "";
    url += parsed.path || "";
    url += queryString ? "?" + queryString : "";
    url += parsed.hash || "";

    return url;
}

/**
 * Parses a URL into an object. The returns object can be mutated to change the URL and
 * then passed to `unparse` to get a URL string again.
 */
export function parse(url: string): NotQuiteAURL {
    let parsedURL = new URL(url, globals.window.location.href);

    let parsed = {
        protocol: parsedURL.protocol,
        host: parsedURL.host,
        searchParams: parsedURL.searchParams,
        path: parsedURL.pathname,
        hash: parsedURL.hash,
    };

    parsed.toString = unparse.bind(null, parsed);

    return parsed;
}

/**
 * Creates a URLSearchParams instance from `obj`.
 */
export function toURLSearchParams(obj: URLQuery | null | undefined): URLSearchParams {
    if (obj instanceof URLSearchParams) {
        return obj;
    }

    if (_.isString(obj)) {
        return new URLSearchParams(obj.replace(/^\?/, ""));
    }

    if (_.isObject(obj) && !_.isArray(obj)) {
        return new URLSearchParams(toQueryString(obj as URLSearchParamsLike));
    }

    return new URLSearchParams();
}

export default {
    URLSearchParams,
    join,
    parse,
    unparse,
    toQueryString,
    toURLSearchParams,
};
